import type AuthenticationType from '@alltrails/types/AuthenticationType';
import type CurrencyCode from '@alltrails/types/CurrencyCode';
import type PurchaseTier from '../../../types/PurchaseTier';
import { getPurchaseEventId, getSignupEventId, getTrialStartEventId } from './conversionsApi';

// The AppsFlyer Web SDK exposes a global `AF` queue (see packages/layout/src/scripts/AppsFlyerPBA.tsx).
// Events are recorded via AF('pba', 'event', { eventType, eventName, eventRevenue, eventRevenueCurrency, eventValue }).
// https://support.appsflyer.com/hc/en-us/articles/43485611331473--Beta-Integrate-AppsFlyer-Web-SDK

// Peak has dedicated trial and purchase events so that AppsFlyer can attribute and optimize on each tier separately.
export const APPSFLYER_SIGNUP_EVENT_NAME = 'signup';
export const APPSFLYER_START_TRIAL_EVENT_NAME = 'af_start_trial';
export const APPSFLYER_START_TRIAL_PEAK_EVENT_NAME = 'af_start_trial_peak';
export const APPSFLYER_PURCHASE_EVENT_NAME = 'af_purchase';
export const APPSFLYER_PURCHASE_PEAK_EVENT_NAME = 'af_purchase_peak';

const isAppsFlyerUnavailable = () => {
  return typeof AF === 'undefined';
};

const setAppsFlyerCustomerUserId = (userId: number) => {
  AF('pba', 'setCustomerUserId', String(userId));
};

export const trackCompleteRegistrationForAppsFlyer = (userId: number, authType: AuthenticationType) => {
  if (isAppsFlyerUnavailable()) {
    return;
  }

  setAppsFlyerCustomerUserId(userId);

  AF('pba', 'event', {
    eventType: 'EVENT',
    eventName: APPSFLYER_SIGNUP_EVENT_NAME,
    eventValue: {
      registration_method: authType,
      event_id: getSignupEventId(userId)
    }
  });
};

export const trackPurchaseForAppsFlyer = ({
  userId,
  currencyCode,
  value,
  isTrial,
  purchaseTier,
  recurlyPlanCode,
  subscriptionEventId
}: {
  userId: number;
  currencyCode: CurrencyCode;
  value: number;
  isTrial: boolean;
  purchaseTier: PurchaseTier;
  recurlyPlanCode?: string;
  subscriptionEventId?: number;
}) => {
  if (isAppsFlyerUnavailable() || !subscriptionEventId) {
    return;
  }

  setAppsFlyerCustomerUserId(userId);

  const isPeak = purchaseTier === 'peak';

  if (isTrial) {
    AF('pba', 'event', {
      eventType: 'EVENT',
      eventName: isPeak ? APPSFLYER_START_TRIAL_PEAK_EVENT_NAME : APPSFLYER_START_TRIAL_EVENT_NAME,
      eventRevenueCurrency: currencyCode,
      eventValue: {
        plan_id: recurlyPlanCode,
        event_id: getTrialStartEventId(subscriptionEventId)
      }
    });
  } else {
    AF('pba', 'event', {
      eventType: 'EVENT',
      eventName: isPeak ? APPSFLYER_PURCHASE_PEAK_EVENT_NAME : APPSFLYER_PURCHASE_EVENT_NAME,
      eventRevenue: value,
      eventRevenueCurrency: currencyCode,
      eventValue: {
        plan_id: recurlyPlanCode,
        event_id: getPurchaseEventId(subscriptionEventId)
      }
    });
  }
};
